/**
 * Hook for proactively refreshing the Supabase session when the user returns after being idle.
 *
 * This prevents "Invalid data" and 401 errors when the user has been away from the
 * website for a while and the JWT token has expired. Instead of waiting for a 401 to
 * trigger refresh (which causes visible errors in Sentry), we proactively refresh when
 * the document becomes visible again after being idle.
 *
 * The extension has an equivalent hook (use-session-refresh.ts) that refreshes via the
 * API client. This web app version refreshes the Supabase session directly.
 */

import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { setStoredToken } from '@/lib/auth/token-utils';
import { logger } from '@/lib/logger';

/** Minimum time (ms) the document must be hidden before refreshing on return */
const MIN_HIDDEN_DURATION_MS = 60_000; // 1 minute

/**
 * Hook that proactively refreshes the Supabase session when the user returns after being idle.
 *
 * When the tab has been hidden for more than 1 minute and becomes visible again,
 * this hook will refresh the Supabase session and update the stored access token.
 * This prevents stale token errors that would otherwise occur on the next API call.
 *
 * @param isAuthenticated - Whether the user is currently authenticated
 */
export function useSessionRefresh(isAuthenticated: boolean): void {
  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Document is being hidden, record the time
        hiddenAtRef.current = Date.now();
      } else {
        // Document is becoming visible
        const hiddenAt = hiddenAtRef.current;
        hiddenAtRef.current = null;

        if (hiddenAt) {
          const hiddenDuration = Date.now() - hiddenAt;

          if (hiddenDuration >= MIN_HIDDEN_DURATION_MS) {
            logger.debug('Tab visible after idle, refreshing session', {
              hiddenDurationMs: hiddenDuration,
            });

            try {
              const { data, error } = await supabase.auth.getSession();
              if (error) {
                logger.debug('Session refresh failed after idle', {
                  error: error.message,
                });
                return;
              }

              if (data.session?.access_token) {
                setStoredToken(data.session.access_token);
                logger.debug('Session refreshed after idle return');
              }
            } catch (error) {
              // Don't throw - this is a proactive refresh, not critical
              logger.debug('Proactive session refresh failed', {
                error: error instanceof Error ? error.message : String(error),
              });
            }
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);
}
